import {
  FEATURE_AREAS,
  FEATURE_AREA_LABELS,
  FEATURE_AVAILABILITY,
  type FeatureArea,
  type FeatureAvailability,
  type FeatureKey,
} from "@bookdental/schemas";
import { toast } from "sonner";
import { Card, CardContent } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { ErroConsulta } from "@/components/ErroConsulta";

const AVAILABILITY_LABEL: Record<FeatureAvailability, string> = {
  NONE: "Não disponível",
  PAID_ONLY: "Plano pago",
  ALL_PLANS: "Todos os planos",
};

type FeatureRow = {
  key: string;
  area: FeatureArea;
  label: string;
  topics: readonly string[];
  status: "active" | "planned";
  availability: FeatureAvailability | null;
};

function LinhaFeature({ feature }: { feature: FeatureRow }) {
  const utils = trpc.useUtils();
  const setAvailability = trpc.admin.planFeatures.set.useMutation({
    onSuccess: () => {
      toast.success(`"${feature.label}" atualizado`);
      utils.admin.planFeatures.list.invalidate();
    },
    onError: (err) => toast.error(err.message || "Falha ao atualizar feature"),
  });

  return (
    <div className="flex flex-col gap-3 border-b border-admin-200 p-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="font-semibold text-admin-900">{feature.label}</p>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {feature.topics.map((topic) => (
            <span
              key={topic}
              className="rounded-full bg-admin-100 px-2 py-0.5 text-[10.5px] font-medium text-admin-700"
            >
              {topic}
            </span>
          ))}
        </div>
      </div>

      {feature.status === "planned" ? (
        <span className="w-fit shrink-0 rounded-md bg-admin-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-admin-700">
          Em breve
        </span>
      ) : (
        <div className="flex shrink-0 gap-1 rounded-lg border border-admin-200 bg-white p-1">
          {FEATURE_AVAILABILITY.map((option) => {
            const active = feature.availability === option;
            return (
              <button
                key={option}
                type="button"
                disabled={setAvailability.isPending}
                onClick={() =>
                  setAvailability.mutate({ feature: feature.key as FeatureKey, availability: option })
                }
                className={cn(
                  "rounded-md px-2.5 py-1.5 text-[11px] font-semibold transition-colors disabled:opacity-50",
                  active
                    ? "bg-admin-900 text-white"
                    : "text-admin-700 hover:bg-admin-100",
                )}
              >
                {AVAILABILITY_LABEL[option]}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AdminPlanFeatures() {
  const { data, isLoading, error } = trpc.admin.planFeatures.list.useQuery();

  const byArea = FEATURE_AREAS.map((area) => ({
    area,
    features: (data ?? []).filter((f) => f.area === area),
  })).filter((group) => group.features.length > 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Planos e features</h1>
        <p className="mt-1 text-sm text-admin-700">
          Escolha quais features cada plano libera — vale a partir da próxima requisição, sem
          deploy. Features "Em breve" ainda não têm implementação real, não dá pra configurar.
        </p>
      </div>

      {error ? (
        <ErroConsulta error={error} />
      ) : isLoading ? (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-admin-900 border-t-transparent" />
        </div>
      ) : (
        byArea.map((group) => (
          <div key={group.area} className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-admin-700">
              {FEATURE_AREA_LABELS[group.area]}
            </h2>
            <Card className="border-admin-200">
              <CardContent className="p-0">
                {group.features.map((feature) => (
                  <LinhaFeature key={feature.key} feature={feature} />
                ))}
              </CardContent>
            </Card>
          </div>
        ))
      )}
    </div>
  );
}
